import { useCallback, useEffect, useRef, useState } from 'react';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

interface UseSearchBarOptions {
  onSearch?: (query: string) => void;
}

interface UseSearchBarReturn {
  query: string;
  open: boolean;
  isLoading: boolean;
  showResults: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputFocus: () => void;
  handleInputBlur: () => void;
  handleResultSelect: () => void;
  setIsMouseOverPopover: (isOver: boolean) => void;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useSearchBar = ({ onSearch }: UseSearchBarOptions = {}): UseSearchBarReturn => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<number>(null);
  const isMouseOverPopoverRef = useRef(false);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);

      if (value.length >= MIN_QUERY_LENGTH) {
        setOpen(true);
      }

      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }

      if (value.length >= MIN_QUERY_LENGTH) {
        setIsLoading(true);
        timeoutRef.current = setTimeout(() => {
          onSearch?.(value);
          setIsLoading(false);
        }, DEBOUNCE_MS);
      } else {
        setIsLoading(false);
      }
    },
    [onSearch]
  );

  const handleInputFocus = useCallback(() => {
    if (query.length >= MIN_QUERY_LENGTH) {
      setOpen(true);
    }
  }, [query]);

  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      if (!isMouseOverPopoverRef.current) {
        setOpen(false);
      }
    }, 200);
  }, []);

  const handleResultSelect = useCallback(() => {
    setOpen(false);
    inputRef.current?.blur();
  }, []);

  const setIsMouseOverPopover = (isOver: boolean): void => {
    isMouseOverPopoverRef.current = isOver;
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  const showResults = open && query.length >= MIN_QUERY_LENGTH;

  return {
    query,
    open,
    isLoading,
    showResults,
    inputRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleResultSelect,
    setIsMouseOverPopover,
    setOpen
  };
};
